// Parish Fellowship design tokens.
// Brand hues (deep blue + gold) sit on a neutral slate scale — the slate is what
// every surface, border and muted label is drawn from.

declare module "@mui/material/styles" {
  interface PaletteColor {
    100: string;
    200: string;
    300: string;
    400: string;
    500: string;
    red: string;
    purple: string;
    yellow: string;
  }
}

// ---------------------------------------------------------------- brand
export const primaryColor = "#004BC6"; // parish blue
export const primaryDark = "#00307E";
export const primaryLight = "#E4EDFB";

export const accentColor = "#F1AB1E"; // parish gold
export const accentDark = "#C98A0C";
export const accentLight = "#FEF6E4";

/** Sidebar / hero gradient, used anywhere the brand needs to fill a surface. */
export const brandGradient = `linear-gradient(180deg, ${primaryColor} 0%, ${primaryDark} 100%)`;
export const brandGradientDiagonal = `linear-gradient(135deg, ${primaryDark} 0%, ${primaryColor} 45%, #1259D2 100%)`;

// ---------------------------------------------------------------- scales
export const primary = {
  100: "#E4EDFB",
  200: "#C0D5F5",
  300: "#92B6EE",
  400: "#4C82DF",
  500: "#1259D2",
  main: primaryColor,
  light: primaryLight,
  dark: primaryDark,
  contrastText: "#ffffff",
  red: "#EF4444",
  purple: "#8B7BF7",
  yellow: accentColor,
};

/** Neutral slate. 300 is the border everything uses, 400 muted text, 500 body text. */
export const secondary = {
  100: "#F9FAFB",
  200: "#F0F4F8",
  300: "#E2E8F0",
  400: "#94A3B8",
  500: "#1E293B",
  main: "#1E293B",
  light: "#F9FAFB",
  dark: "#0F172A",
  contrastText: "#ffffff",
  red: "#EF4444",
  purple: "#8B7BF7",
  yellow: accentColor,
};

// ---------------------------------------------------------------- semantic
export const error = { main: "#EF4444", light: "#FEE2E2", dark: "#B91C1C" };
export const success = { main: "#10B981", light: "#D1FAE5", dark: "#047857" };
export const warning = { main: accentColor, light: accentLight, dark: accentDark };
export const info = { main: "#0EA5E9", light: "#E0F2FE", dark: "#0369A1" };

export const text = {
  primary: secondary[500],
  secondary: secondary[400],
  disabled: secondary[300],
};

// ---------------------------------------------------------------- surfaces
export const surface = {
  page: "#F0F4F8",
  paper: "#FFFFFF",
  subtle: "#F8FAFC",
  border: secondary[300],
  borderSubtle: "#F0F4F8",
};

/** Tinted icon/avatar backgrounds for stat cards and list rows. */
export const tint = {
  blue: "#E4EDFB",
  gold: accentLight,
  green: "#D1FAE5",
  red: "#FEE2E2",
  sky: "#E0F2FE",
  purple: "#EDE9FE",
  slate: "#F1F5F9",
};

/**
 * Categorical chart series, assigned in this fixed order and never cycled.
 * Verified with the dataviz palette validator against a white chart surface:
 * lightness band, chroma floor, adjacent-pair CVD separation (worst ΔE 8.6
 * deutan), normal-vision floor and 3:1 contrast all pass. Re-run the validator
 * before changing any value or the order — the ordering is what makes it pass.
 * Past 8 series, fold the tail into "Other" rather than adding a 9th hue.
 */
export const chartColors = [
  "#004BC6", // brand blue
  "#B4820E", // gold
  "#0891B2", // cyan
  "#DC2626", // red
  "#059669", // green
  "#7C5CE0", // purple
  "#EA580C", // orange
  "#BE185D", // magenta
];

/** Single-hue default for magnitude comparisons (bars, most of the time). */
export const chartPrimary = primaryColor;

/** Recessive chrome for axes, gridlines and tick labels. */
export const chartGrid = "#E2E8F0";
export const chartAxisText = secondary[400];
